import { useQuery } from "@tanstack/react-query";
import {
  Cube2BOrder,
  Cube2COrder,
  IntegrationOrderSearchParams,
  IntegrationOrderType,
} from "@/types";
import { queryOrders as queryIntegrationOrders } from "@/apis/integration/order";
import { query2BOrders, query2COrders } from "@/apis/cube/order";

export function useIntegrationOrders(query: IntegrationOrderSearchParams) {
  return useQuery({
    queryKey: ["integration-orders", JSON.stringify(query)],
    queryFn: () => queryIntegrationOrders(query),
    placeholderData: (previousData) => previousData,
  });
}

export function getAssociatedCubeOrder(
  customerOrderNo: string | undefined,
  type: IntegrationOrderType | undefined,
): Promise<Cube2COrder | Cube2BOrder | null> {
  if (!customerOrderNo || !type) {
    return Promise.resolve(null);
  }
  if (type === IntegrationOrderType.Customer) {
    return query2COrders({ theThirdNumber: customerOrderNo }).then((res) => {
      if (res.items.length > 0) {
        return res.items[0] as Cube2COrder;
      }
      return null;
    });
  } else {
    return query2BOrders({ customerNumber: customerOrderNo }).then((res) => {
      if (res.items.length > 0) {
        return res.items[0] as Cube2BOrder;
      }
      return null;
    });
  }
}

export function searchCubeOrders(
  type: IntegrationOrderType,
  fuzzyContent: string | undefined,
): Promise<{ label: string; value: string }[]> {
  if (type === IntegrationOrderType.Customer) {
    const params = fuzzyContent
      ? { instructionNumber: fuzzyContent.trim() }
      : undefined;
    return query2COrders(params).then((res) => {
      return res.items.map((item) => {
        return {
          label: `${item.instruction.instructionNumber} (${item.instruction.theThirdNumber})`,
          value: `${item.userInfo.userCode}@${item.instruction.instructionId}`,
        };
      });
    });
  } else if (type === IntegrationOrderType.Business) {
    const params = fuzzyContent
      ? { deliveryNos: [fuzzyContent.trim()] }
      : undefined;
    return query2BOrders(params).then((res) => {
      return res.items.map((item) => {
        let suffix = "N/A";
        if (
          item?.amazonFreightNumber != null &&
          item.amazonFreightNumber !== ""
        ) {
          suffix = item.amazonFreightNumber;
        }
        let userCode = "N/A";
        if (item?.userCode != null && item.userCode !== "") {
          userCode = item.userCode;
        }
        return {
          label: `${userCode} - ${item.deliverNumber} (${suffix})`,
          value: `${item.userCode}@${item.boxStockDeliverId}`,
        };
      });
    });
  }
  return Promise.resolve([]);
}

export function useSearchCubeOrders(
  type: IntegrationOrderType | undefined,
  fuzzyContent: string | undefined,
) {
  return useQuery({
    queryKey: ["search-cube-orders", type, fuzzyContent],
    queryFn: () => searchCubeOrders(type!, fuzzyContent),
    enabled: !!type,
  });
}
